import React from "react";
import classNames from "classnames";
import { useTheme } from "./theme";
import { useI18n } from "../lib/i18n/i18n_provider";

type TextSize = "xl" | "lg" | "md" | "sm" | "xs";
export type TextColor =
  | "white"
  | "default"
  | "muted"
  | "link"
  | "success"
  | "error"
  | "primary"
  | "warning";
type TextDecoration = "overline" | "line-through" | "underline" | "";
export type TextWeight =
  | "normal"
  | "bold"
  | "300"
  | "400"
  | "500"
  | "600"
  | "700"
  | "800"
  | "900";

interface TextProps {
  children?: React.ReactNode;
  size?: TextSize;
  color?: TextColor;
  align?: "left" | "right" | "center";
  weight?: TextWeight;
  italic?: boolean;
  testID?: string;
  decoration?: TextDecoration;
  overflow?: "ellipsis";
  className?: string;
  nowrap?: boolean;
  onPress?: () => void;
  /** Lets a control point `aria-describedby` at this run of text. */
  id?: string;
}

/**
 * Run of text with a single style.
 */
export function Text(props: TextProps) {
  const {
    align,
    children,
    color = "default",
    size = "md",
    overflow,
    decoration,
    className,
    testID,
    weight,
    italic,
    nowrap,
    onPress,
    id,
  } = props;
  const { locale } = useI18n();
  const theme = useTheme();

  return (
    <span
      id={id}
      data-testid={testID}
      onClick={onPress}
      className={classNames(
        "text",
        color && `text-color-${color}`,
        size && `text-size-${size}`,
        align && `text-align-${align}`,
        decoration && `text-decoration-${decoration}`,
        weight && `text-weight-${weight}`,
        overflow && `text-overflow-${overflow}`,
        nowrap && `text-nowrap`,
        !!onPress && `text-has-press`,
        `text-${locale.baseLanguageTag}`,
        className
      )}
      style={{
        fontFamily: theme.fontFamilyBody,
        fontStyle: italic ? "italic" : "normal",
      }}
    >
      {children}
    </span>
  );
}
